/* eslint-disable no-continue */

'use client'

import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useElementVisible } from '@hooks/useElementVisible'
import { EMPTY_POLYGON, FILLED_POLYGON } from '@constants/aboutUs'
import { Position } from '@enums/Position'
import { WalkthroughMock } from '@components/AboutUs/AboutUsMock'
import styles from './Walkthrough.module.scss'

const Walkthrough = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [animateImage, setAnimateImage] = useState(false)
  const timelineItemsRef = useRef<Array<HTMLDivElement | null>>([])
  const rightPanelRef = useRef<HTMLDivElement | null>(null)
  const topHeaderSection = useElementVisible()

  useEffect(() => {
    const section = containerRef.current
    const timeline = timelineRef.current
    const wrapper = wrapperRef.current
    const rightPanel = rightPanelRef.current

    if (!section || !timeline || !wrapper || !rightPanel) return

    const isHorizontal = window.innerWidth > 756

    if (isHorizontal) {
      const horizontalScrollLength = timeline.scrollWidth - window.innerWidth / 2
      section.style.height = `${window.innerHeight + horizontalScrollLength}px`

      const handleScroll = () => {
        const scrollY = window.scrollY
        const sectionTop = section.offsetTop
        const sectionHeight = window.innerHeight + horizontalScrollLength
        const sectionBottom = sectionTop + sectionHeight
        const windowWidth = window.innerWidth

        if (scrollY >= sectionTop && scrollY <= sectionBottom) {
          const scrollOffset = scrollY - sectionTop
          timeline.style.transform = `translateX(-${
            Math.min(scrollOffset, horizontalScrollLength) + windowWidth / 12
          }px)`
          wrapper.style.position = Position.STICKY
          wrapper.style.top = '0'
        } else if (scrollY > sectionBottom) {
          timeline.style.transform = `translateX(-${horizontalScrollLength}px)`
          wrapper.style.position = Position.RELATIVE
        } else {
          timeline.style.transform = 'translateX(0)'
          wrapper.style.position = Position.RELATIVE
        }

        const rightRect = rightPanel.getBoundingClientRect()
        const leftPanelEdge = rightRect.left

        for (let i = 0; i < timelineItemsRef.current.length; i++) {
          const item = timelineItemsRef.current[i]
          if (!item) continue

          const itemRect = item.getBoundingClientRect()
          const distance = itemRect.left - leftPanelEdge

          if (distance > 0 && distance < 420) {
            if (i !== currentIndex) {
              setAnimateImage(true)
              setCurrentIndex(i)
              setTimeout(() => setAnimateImage(false), 800)
            }
            break
          }
        }
      }

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
    } else {
      const wrapperHeight = wrapper.offsetHeight
      const timelineHeight = timeline.scrollHeight
      const scrollableTimelineHeight = timelineHeight - wrapperHeight

      const scrollLength = scrollableTimelineHeight + 500
      const totalHeight = wrapperHeight + scrollLength

      section.style.height = `${totalHeight}px`

      const handleScroll = () => {
        const scrollY = window.scrollY
        const sectionTop = section.offsetTop
        const sectionBottom = sectionTop + totalHeight

        if (scrollY >= sectionTop && scrollY <= sectionBottom) {
          const relativeScroll = scrollY - sectionTop
          const translateY = Math.min(relativeScroll, timelineHeight - 150)

          timeline.style.transform = `translateY(-${translateY}px)`
          wrapper.style.position = Position.STICKY
          wrapper.style.top = '0'
        } else if (scrollY > sectionBottom) {
          timeline.style.transform = `translateY(-${scrollableTimelineHeight}px)`
          wrapper.style.position = Position.RELATIVE
          wrapper.style.top = 'unset'
        } else {
          timeline.style.transform = 'translateY(0px)'
          wrapper.style.position = Position.RELATIVE
          wrapper.style.top = 'unset'
        }

        const rightRect = rightPanel.getBoundingClientRect()
        const leftPanelEdge = rightRect.top

        for (let i = 0; i < timelineItemsRef.current.length; i++) {
          const item = timelineItemsRef.current[i]
          if (!item) continue

          const itemRect = item.getBoundingClientRect()
          const distance = itemRect.top + itemRect.height / 2 - leftPanelEdge

          if (distance > 0 && distance < 200) {
            if (i !== currentIndex) {
              setAnimateImage(true)
              setCurrentIndex(i)
              setTimeout(() => setAnimateImage(false), 800)
            }
            break
          }
        }
      }

      window.addEventListener('scroll', handleScroll)
      return () => window.removeEventListener('scroll', handleScroll)
    }
  }, [currentIndex])

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div className={styles.stickyContainer} ref={wrapperRef}>
        <div className={styles.leftPanel}>
          <Image
            src={WalkthroughMock?.journeyData[currentIndex].image}
            alt="Journey"
            width={500}
            height={700}
            className={`${styles.image} ${animateImage ? styles.imageAnimate : ''}`}
          />
        </div>

        <div className={styles.rightPanel}>
          <div
            className={`${styles.topHeader} ${topHeaderSection.isVisible ? styles.visible : ''}`}
            ref={topHeaderSection.ref}>
            <span className={styles.capitalText}>{WalkthroughMock?.title}</span>
            <span className={styles.heading}>{WalkthroughMock?.description}</span>
          </div>

          <div className={styles.rightImage}>
            <Image
              src={WalkthroughMock?.journeyData[currentIndex].image2}
              alt="Journey"
              width={736}
              height={400}
              className={`${animateImage ? styles.imageAnimate : ''}`}
            />
          </div>

          <div className={styles.timelineWrapper} ref={rightPanelRef}>
            <div className={styles.timeline} ref={timelineRef}>
              {WalkthroughMock?.journeyData.map((item, i) => (
                <div
                  className={`${styles.timelineItem} ${currentIndex === i ? styles.activeTime : ''}`}
                  key={i}
                  data-index={i}
                  ref={el => (timelineItemsRef.current[i] = el)}>
                  <div className={styles.year}>{item.year}</div>
                  <p className={styles.text}>{item.text}</p>
                  <Image
                    src={currentIndex === i ? FILLED_POLYGON : EMPTY_POLYGON}
                    alt="Polygon"
                    width={16}
                    height={16}
                    className={styles.timelinePolygon}
                  />
                </div>
              ))}
            </div>
            <div className={styles.progressBar} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Walkthrough
